
import '../src/global.css'
import {SavedCards} from './SavedCards';
import { SavedCardsWithoutSession } from './SavedCardsWithoutSession';
import {cardTypeDetector, setupCardTypeDetection} from './utils/cardTypeUtil';
import { isValidCardNumber, isValidExpiryDate, validateCardName, validateCardNumber, validateCVC, validateExpiryDate } from './utils/cardValidationUtil';
import { createIframe } from './utils/iframeUtil';
import { convertData, encryptString, getApiBaseUrl } from './utils/stringUtil';

const paymid = {};

// Configuration object for the SDK
const config = {
  hosted_baseurl: "https://cdn.jsdelivr.net/npm/paymid-sdk@1.0.1",
  defaultSupportedCards: ["VISA", "MASTERCARD", "AMEX"],
  termsAndCondition: "https://paymid.com/terms-and-conditions/",
  currency: '',
  amount: '',
  token: '',
  error_codes: {
    MISSING_CONTAINER_ID: "Container ID is required and must exist in the DOM.",
    MISSING_SESSION_ID: "Valid session ID is required.",
    MISSING_CARD_BRAND: "Invalid Card brand type",
    MISSING_MERCHANT_CRED: "Missing merchant credential"
  },
};

const checkForSavedCardsWithoutSessionId = (obj) => {
  if(obj?.merchantAccountId && obj?.customerReferenceId && obj?.email && obj?.APP_KEY && obj?.SECRET_KEY && obj?.token){
    return true;
  }
  return false;
}


// Main create function to render the hosted form
async function cardForm(options) {
  const {
    containerId,
    savedCardContainerId,
    styles = {},
    onSubmit,
    onFieldEvent,
    sessionID,
    cardBrandType = config.defaultSupportedCards,
    resetAfterSubmit = false,
    autoHideForm = false,
    environment,
    payloadCallback = null,
    merchantAccountId = null,
    appKey = null,
    secretKey = null,
    buttonId,
    serverToServer = false,
    savedCardStyle = {},
    savedCardsWithoutSessionId = {}
  } = options;
  
  // Set API base URL based on environment
  config.api_baseurl = getApiBaseUrl(environment, config.error_codes.INVALID_ENVIRONMENT);

  if (!containerId || !document.getElementById(containerId)) {
    throw Object.assign(
      new Error(config?.error_codes?.MISSING_CONTAINER_ID),
      { code: "MISSING_CONTAINER_ID" }
    );
  }

  if (!serverToServer && !sessionID) {
    throw Object.assign(
      new Error(config?.error_codes?.MISSING_SESSION_ID),
      { code: "MISSING_SESSION_ID" }
    );
  }

  if (
    typeof payloadCallback === "function" &&
    (!appKey || !secretKey || !merchantAccountId)
  ) {
    throw Object.assign(
      new Error(config?.error_codes?.MISSING_MERCHANT_CRED),
      { code: "MISSING_MERCHANT_CRED" }
    );
  }
  

  // Validate cardBrandType
  const supportedCards = cardBrandType
    .map((type) => type.toUpperCase())
    .filter((type) => config?.defaultSupportedCards.includes(type));
  if (supportedCards.length === 0) {
    throw Object.assign(
        new Error(config?.error_codes?.MISSING_CARD_BRAND),
        { code: "MISSING_CARD_BRAND" }
      );
  }

  try {
    // Create form container
    let detectedCardType = "";
    const container = document.getElementById(containerId);
    container.innerHTML = "";
    let decryptedData = ''

    if(!serverToServer) {
      const response = await fetch(`${config?.api_baseurl}/payment/info/${sessionID}`, {
        method: "GET",
      });
      if (!response.ok) {
        throw new Error("Failed to fetch payment info: " + response.statusText);
      }
      const jsonResponse = await response.json();
      decryptedData = await convertData(jsonResponse.data);
      
      if (decryptedData?.status !== 'Pending') {
          throw Object.assign(
              new Error(config?.error_codes?.MISSING_SESSION_ID),
              { code: "MISSING_SESSION_ID" }
          );
      }
      config.amount = decryptedData?.amount
      config.currency = decryptedData?.currency?.sign
      if(decryptedData?.merchant?.merchant_settings?.terms_and_condition_url){
          config.termsAndCondition = decryptedData?.merchant?.merchant_settings?.terms_and_condition_url;
      }
    }

    // Form HTML structure with customizable error message styles
    const labelColor = styles.labelColor || "#000";
    const errorColor = styles.errorMessage?.color || "red";
    const errorFontSize = styles.errorMessage?.fontSize || "12px";
    const customButtonStyle =
      styles.buttonStyle ||
      `
      background-color: #212529;
      color: #fff;
      padding: 14px;
      width: 100%;
      border: none;
      border-radius: 4px;
      cursor: pointer;
    `;

    const form = document.createElement("form");
    form.innerHTML = `
      <div style="margin-bottom: 20px;">
        <div style="width: 100%;">
          <label for="card" style="margin-bottom: 8px; display: block; color: ${labelColor};">Card Number</label>
          <div id="card-container" style="position: relative;">
            <div class="logo-container" style="position: absolute; top: 50%; right: 10px; transform: translateY(-50%); display: flex; align-items: center;"></div>
          </div>
          <div class="error-message" id="card-error" style="color: ${errorColor}; font-size: ${errorFontSize};"></div>
        </div>
      </div>
      <div style="display: flex; gap: 30px; margin-bottom: 12px;">
        <div style="width: 50%;">
          <label for="expiry_date" style="margin-bottom: 8px; display: block; color: ${labelColor};">Expiry Date</label>
          <div id="expiry-container"></div>
          <div class="error-message" id="expiry-error" style="color: ${errorColor}; font-size: ${errorFontSize};"></div>
        </div>
        <div style="width: 50%;">
          <label for="secret" style="margin-bottom: 8px; display: block; color: ${labelColor};">CVC</label>
          <div id="secret-container"></div>
          <div class="error-message" id="secret-error" style="color: ${errorColor}; font-size: ${errorFontSize};"></div>
        </div>
      </div>
      <div style="margin-bottom: 20px;">
        <div style="width: 100%;">
          <label for="card_name" style="margin-bottom: 8px; display: block; color: ${labelColor};">Name on Card</label>
          <input type="text" id="card_name" placeholder="Full name as written on card" style="
            padding: 10px;
            margin-bottom: ${styles.cardName?.marginBottom || '8px'};
            border: ${styles.cardName?.border || '1px solid #ccc'};
            border-radius: ${styles.cardName?.borderRadius || '4px'};
            font-size: ${styles.cardName?.fontSize || '16px'};
            color: ${styles.cardName?.color || '#000'};
            background: ${styles.cardName?.background || '#fff'};
            height: ${styles.cardName?.height || '40px'};
            width: 100%;
            box-sizing: border-box;
            display: flex;
            align-items: center;
            text-align: left;
            line-height: ${styles.cardName?.height || '40px'};
          "/>
          <div class="error-message" id="card_name-error" style="color: ${errorColor}; font-size: ${errorFontSize};"></div>
        </div>
      </div>
      <div style="display: none; align-items: center; margin-bottom: 20px;" id='saved-card-wrapper'>
        <input type="checkbox" id="savedCard" style="margin-right: 8px;" />
        <label for="savedCard" style="color: ${labelColor};">
          Save card for faster transaction
        </label>
      </div>
      <div style="display: flex; align-items: center; margin-bottom: 20px;">
        <input type="checkbox" id="isReadCondition" style="margin-right: 8px;" />
        <label for="isReadCondition" style="color: ${labelColor};">
          I agree to the <a href="${config?.termsAndCondition}" target="_blank">Terms and Conditions</a>
        </label>
      </div>
      <div id="button-container"></div>`;
    container.appendChild(form);

    if(savedCardContainerId && decryptedData?.payment_checkout_token){
      config.token = decryptedData?.payment_checkout_token;
      const savedCardContainer = document.getElementById("saved-card-wrapper");
      savedCardContainer.style.display = 'flex';
      SavedCards(savedCardContainerId, decryptedData?.payment_checkout_token, sessionID, config.api_baseurl, onSubmit, config.hosted_baseurl, savedCardStyle)
    } else if(savedCardContainerId && checkForSavedCardsWithoutSessionId(savedCardsWithoutSessionId)){
      config.token = savedCardsWithoutSessionId?.token;
      const savedCardContainer = document.getElementById("saved-card-wrapper");
      savedCardContainer.style.display = 'flex';
      SavedCardsWithoutSession(savedCardContainerId, savedCardsWithoutSessionId, config?.api_baseurl, onSubmit, config?.hosted_baseurl, savedCardStyle)
    }

    const buttonContainer = document.getElementById("button-container");
    let submitButton;
    if (buttonId && document.getElementById(buttonId)) {
      // Use the provided buttonId and hide the default button container
      submitButton = document.getElementById(buttonId);
      buttonContainer.style.display = 'none';
    } else {
      // Create default submit button
      submitButton = document.createElement("button");
      submitButton.setAttribute("type", "submit");
      submitButton.textContent = `Pay ${config?.currency} ${config?.amount}`;
      submitButton.style.cssText = customButtonStyle;
      buttonContainer.appendChild(submitButton);
    }
    

    // Append iframes for sensitive fields
    const cardContainer = form.querySelector("#card-container");
    const expiryContainer = form.querySelector("#expiry-container");
    const secretContainer = form.querySelector("#secret-container");
    const cardNameInput = form.querySelector("#card_name");

    cardContainer.appendChild(
      createIframe("card", "1234 1234 1234 1234", styles.card || {})
    );
    expiryContainer.appendChild(
      createIframe("expiry_date", "MM/YY", styles.expiry || {})
    );
    secretContainer.appendChild(
      createIframe("secret", "123", styles.secret || {})
    );

    // Track typing state for each field
    const fieldStates = {
      card: { hasTyped: false },
      expiry_date: { hasTyped: false },
      secret: { hasTyped: false },
      card_name: { hasTyped: false }
    };

    // Cardholder name validation
    cardNameInput.addEventListener("input", (e) => {
      fieldStates.card_name.hasTyped = true;
      const value = (e.target.value || "").replace(/[^a-zA-Z\s]/g, "").toUpperCase();
      e.target.value = value;
      if (onFieldEvent) {
        onFieldEvent({ field: "card_name", value });
      }
    });

    cardNameInput.addEventListener("blur", () => {
      if (fieldStates.card_name.hasTyped) {
        validateCardName(cardNameInput.value || "", form);
      }
    });

    // Setup card type detection and logo display
    setupCardTypeDetection(cardContainer, supportedCards, config?.hosted_baseurl, (event) => {
      if (event?.cardType) {
        detectedCardType = event.cardType.toLowerCase();
      }
      if (onFieldEvent) {
        onFieldEvent(event);
      }
    });

    // Setup validation for iframe fields
    const setupFieldValidation = (iframeId, validateFn) => {
      window.addEventListener("message", (event) => {
        if (event.data.field === iframeId) {
          const value = (event.data.value || "").replace(/\s/g, "");
          if (event.data.event === "input") {
            fieldStates[iframeId].hasTyped = event.data.hasTyped;
            // Do not validate or clear errors during input
          } else if (event.data.event === "blur" && fieldStates[iframeId].hasTyped) {
            // Validate and show/clear error only on blur
            validateFn(value, form, detectedCardType);
          }
        }
      });
    };

    setupFieldValidation("card", (value, form) =>
      validateCardNumber(value, form, supportedCards)
    );
    setupFieldValidation("expiry_date", validateExpiryDate);
    setupFieldValidation("secret", (value, form, cardType) =>
      validateCVC(value, form, cardType)
    );

    const handleSubmit =  async (e) => {
      if (e) {
        e.preventDefault();
      }
      const isReadCondition = form.querySelector("#isReadCondition").checked;
      const isSavedCard = form.querySelector("#savedCard").checked;
      if (!isReadCondition) {
        alert("Please agree to the Terms and Conditions.");
        return;
      }
    
      // Collect field values
      const getIframeValue = (iframeId, inputId) => {
        const iframe = document.getElementById(`cardform-${iframeId}`);
        const iframeDoc =
          iframe.contentDocument || iframe.contentWindow.document;
        return (iframeDoc.getElementById(inputId)?.value || "").replace(/\s/g, "");
      };
    
      const cardData = {
        card: getIframeValue("card", "card-input"),
        expiry_date: getIframeValue("expiry_date", "expiry_date-input"),
        secret: getIframeValue("secret", "secret-input"),
        card_name: (form.querySelector("#card_name").value || "").toUpperCase(),
        id: sessionID,
        save_card: false,
        isReadCondition: true,
        payUsingExistingCard: false,
        card_type: detectedCardType,
      };
    
      // Validation
      let isValid = true;
      const cardType = cardTypeDetector(cardData.card);
    
      if (!cardData.card) {
        form.querySelector("#card-error").textContent = "Card number is required";
        isValid = false;
      } else if (!isValidCardNumber(cardData.card)) {
        form.querySelector("#card-error").textContent = "Invalid card number";
        isValid = false;
      } else if (!cardType || !supportedCards.includes(cardType)) {
        form.querySelector("#card-error").textContent = `Card must be ${supportedCards.join(", ")}`;
        isValid = false;
      }
    
      if (!cardData.expiry_date) {
        form.querySelector("#expiry-error").textContent = "Expiry date is required";
        isValid = false;
      } else if (!isValidExpiryDate(cardData.expiry_date)) {
        form.querySelector("#expiry-error").textContent = "Invalid date";
        isValid = false;
      }
    
      if (!cardData.secret) {
        form.querySelector("#secret-error").textContent = "CVC is required";
        isValid = false;
      } else if (!cardData.secret.match(/^\d{3,4}$/)) {
        form.querySelector("#secret-error").textContent = "Invalid CVC";
        isValid = false;
      } else if (cardType === "AMEX" && cardData.secret.length !== 4) {
        form.querySelector("#secret-error").textContent = "Amex CVC must be 4 digits";
        isValid = false;
      } else if (cardType !== "AMEX" && cardData.secret.length !== 3) {
        form.querySelector("#secret-error").textContent = "CVC must be 3 digits";
        isValid = false;
      }
    
      if (!cardData.card_name) {
        form.querySelector("#card_name-error").textContent = "Cardholder name is required";
        isValid = false;
      } else if (!cardData.card_name.match(/^[a-zA-Z\s]+$/)) {
        form.querySelector("#card_name-error").textContent = "Name must contain only letters and spaces";
        isValid = false;
      }
    
      if (!isValid) return;
    
      try {
        if(payloadCallback && typeof payloadCallback === "function"){
          const basicToken = btoa(`${appKey}:${secretKey}`);
          const response = await fetch(`${config?.api_baseurl}/payload-encryption-public-key?merchantAccountId=${merchantAccountId}`, {
            method: "GET",
            headers: {
                  "Authorization": `Basic ${basicToken}`,
            },
          });
          if (!response.ok) {
            throw new Error("Failed to fetch payment info: " + response.statusText);
          }
          const publicKey = await response.json();
          const cardInfo = {
            card: encryptString(cardData.card, publicKey?.data),
            secret: encryptString(cardData.secret, publicKey?.data),
            expiry_date: encryptString(cardData.expiry_date, publicKey?.data),
            card_name: encryptString(cardData.card_name, publicKey?.data)
          }
          cardInfo.is_hosted_fields_encrypted = true;
          if(sessionID){
            cardInfo.id = sessionID;
            cardInfo.payment_request_id = sessionID;
          }
          if(isSavedCard && config?.token) {
            cardInfo.checkout_token = config?.token;
            cardInfo.save_card = true;
          }
          payloadCallback(cardInfo);       
        }else{
          if(isSavedCard && config?.token) {
            cardData.checkout_token = config?.token;
            cardData.save_card = true;
          }
          const response = await fetch(
            `${config?.api_baseurl}/payment/process`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(cardData),
            }
          );
          if (!response.ok) {
            throw new Error("Payment processing failed: " + response.statusText);
          }
          const result = await response.json();
          if (onSubmit && typeof onSubmit === 'function' ) {
            onSubmit({ status: "success", data: result });
          } 
        }
       
        if (resetAfterSubmit) {
          form.reset();
          ["card", "expiry_date", "secret"].forEach((id) => {
            const iframe = document.getElementById(`cardform-${id}`);
            const input = iframe?.contentDocument?.getElementById(`${id}-input`);
            if (input) input.value = "";
          });
        }
    
        if (autoHideForm) {
          container.innerHTML = "";
        }
       
      } catch (error) {
        if (onSubmit && !payloadCallback) {
          onSubmit({ status: "failed", data: error });
        }
        if (resetAfterSubmit) {
          form.reset();
          ["card", "expiry_date", "secret"].forEach((id) => {
            const iframe = document.getElementById(`cardform-${id}`);
            const input = iframe?.contentDocument?.getElementById(`${id}-input`);
            if (input) input.value = "";
          });
        }
        throw new Error("Payment error: " + error.message);
      }
    }

    // Form submission handler
    form.addEventListener("submit", handleSubmit);
    if (buttonId && document.getElementById(buttonId)) {
      submitButton.addEventListener("click", handleSubmit);
    } else {
      form.addEventListener("submit", handleSubmit);
    }
  } catch (error) {
    if (onSubmit && error.code !== "MISSING_SESSION_ID" && error.code !== "MISSING_CONTAINER_ID" && error.code !== 'MISSING_CARD_BRAND' && error.code !== "MISSING_MERCHANT_CRED") {
      onSubmit({ status: "failed", data: error });
    }
    console.error(error);
  }
}

paymid.cardForm = cardForm;

export default paymid;